import React, { useEffect, useState } from 'react'

import { GettingQuoteFailed } from './GetErrorBanners'
import { QuoteCreateGenerateBenchmark } from './QuoteCreateBenchmark'
import { QuoteCreateCensusDetails } from './QuoteCreateCensusDetails'
import { QuoteCreateCensusUpload } from './QuoteCreateCensusUpload'
import { QuoteCreateGenerateQuote } from './QuoteCreateGenerateQuote'
import { QuoteCreateResult } from './QuoteCreateResult'
import { type Quote } from './types'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useGetQuote } from '../hooks/useGetQuote'

type Props = {
  prospectId: string;
  quoteId: string;
  enableQuotingComplianceEscapeHatch?: boolean;
}

export function doesBenchmarksExist (data: Quote | null | undefined) {
  return (
    data &&
    data.target_individual_deductible &&
    data.target_individual_oop_max
  )
}

export function hasQuoteGenerationStarted (data: Quote | null | undefined) {
  return (
    data &&
    data.metal_tiers &&
    data.metal_tiers.length > 0 &&
    data.coverage_percentiles &&
    data.coverage_percentiles.length > 0
  )
}

/**
 * When this page is first rendered:
 * - If the quote does not have census and benchmark, we show census upload
 * - If the quote has census but not benchmark, we show census details
 * - If the quote has census and benchmark, we show benchmark details (final quote generation)
 * Stage management is handled via stage state
 */
function QuoteDetailsImpl ({ prospectId, quoteId, enableQuotingComplianceEscapeHatch = false }: Props) {
  const { data, error, isLoading } = useGetQuote({ prospectId, quoteId })
  const [stage, setStage] = useState<string>('loading')
  const [quoteAPIError, setQuoteAPIError] = useState<
  GrapeAPIErrorType | undefined
  >()
  // TODO: improve the loading state and empty census state

  useEffect(() => {
    const dataLoaded = !isLoading && data && !error
    const benchmarkExists = doesBenchmarksExist(data)
    const quoteGenerationStarted = hasQuoteGenerationStarted(data)
    if (
      dataLoaded &&
      !data.quoting_prospect_employee_census_id &&
      !benchmarkExists &&
      !quoteGenerationStarted &&
      !quoteAPIError
    ) {
      setStage('census_upload')
    } else if (
      dataLoaded &&
      data.quoting_prospect_employee_census_id &&
      !benchmarkExists &&
      !quoteGenerationStarted &&
      !quoteAPIError
    ) {
      setStage('census_details')
    } else if (
      dataLoaded &&
      data.quoting_prospect_employee_census_id &&
      benchmarkExists &&
      !quoteGenerationStarted &&
      !quoteAPIError
    ) {
      setStage('benchmark_show')
    } else if (
      dataLoaded &&
      data.quoting_prospect_employee_census_id &&
      (quoteGenerationStarted || quoteAPIError)
    ) {
      setStage('quote_done')
    }
  }, [data, error, isLoading])

  if (!isLoading && error) {
    return (
      <div>
        <h1 className='heading-lg pb-10'>Create New Quote</h1>
        <GettingQuoteFailed errorObj={error} />
      </div>
    )
  }

  return (
    <div>
      <h1 className='heading-lg pb-10'>Create New Quote</h1>
      {isLoading && <div>Loading...</div>}
      {stage === 'census_upload' && (
        <QuoteCreateCensusUpload prospectId={prospectId} quoteId={quoteId} />
      )}
      {stage === 'census_details' && (
        <QuoteCreateCensusDetails
          prospectId={prospectId}
          quoteId={quoteId}
          censusId={data!.quoting_prospect_employee_census_id}
          onCensusUploadDone={() => {
            setStage('benchmark_create')
          }}
        />
      )}
      {stage === 'benchmark_create' && (
        <QuoteCreateGenerateBenchmark
          prospectId={prospectId}
          quoteId={quoteId}
          onGoBackToCensus={() => {
            setStage('census_details')
          }}
          onSkipBenchmarkGeneration={() => {
            if (doesBenchmarksExist(data)) {
              // if the benchmark exist we will still show the benchmark
              setStage('benchmark_show')
            } else {
              setStage('benchmark_skip')
            }
          }}
          onSuccess={() => {
            setStage('benchmark_show')
          }}
        />
      )}
      {(stage === 'benchmark_show' || stage === 'benchmark_skip') && (
        <QuoteCreateGenerateQuote
          skipBenchmark={stage === 'benchmark_skip'}
          prospectId={prospectId}
          quoteId={quoteId}
          onGoBackToBenchmarkParameters={() => {
            setStage('benchmark_create')
          }}
          onSuccess={() => {
            setStage('quote_done')
            setQuoteAPIError(undefined)
          }}
          onFailure={(error) => {
            setStage('quote_done')
            setQuoteAPIError(error)
          }}
          enableQuotingComplianceEscapeHatch={enableQuotingComplianceEscapeHatch}
        />
      )}
      {stage === 'quote_done' && (
        <QuoteCreateResult
          quote={data!}
          quoteAPIError={quoteAPIError}
          handleBack={() => {
            if (doesBenchmarksExist(data)) {
              setStage('benchmark_show')
            } else {
              setStage('benchmark_skip')
            }
          }}
        />
      )}
    </div>
  )
}

/**
 * This component is used for quote details page, which is accessible from quote list
 * and the page URL. Given that this is a top level component we use QueryClientProvider
 */
export default function QuoteDetails ({ prospectId, quoteId, enableQuotingComplianceEscapeHatch = false }: Props) {
  return (
    <QuoteDetailsImpl prospectId={prospectId} quoteId={quoteId} enableQuotingComplianceEscapeHatch={enableQuotingComplianceEscapeHatch} />
  )
}
